import { useTranslation } from 'react-i18next';
import * as React from "react";
import { trDisplay } from '../../i18n';
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";

export const TooltipPortalContainerContext = React.createContext(undefined);

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) =>
  { useTranslation(); const portalContainer = React.useContext(TooltipPortalContainerContext); return <TooltipPrimitive.Portal container={portalContainer}>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={12}
      tabIndex={0}
      className={cn(
        "z-50 max-h-[min(76vh,42rem)] overflow-x-hidden overflow-y-auto overscroll-contain [scrollbar-gutter:stable] rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground outline-none focus-visible:ring-2 focus-visible:ring-white/70 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>; }
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// HelpCircle icon component
const HelpCircleIcon = ({ className }) =>
  { useTranslation(); return <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </svg>; }
;

/**
 * Mobile-friendly tooltip that uses Popover on touch devices
 * This approach is more reliable than trying to intercept touch events on Radix Tooltip
 *
 * On desktop: hover to show tooltip (uses Radix Tooltip)
 * On touch devices: tap to toggle popover open/closed (uses Radix Popover)
 */
const MobileTooltip = ({
  children,
  content,
  side = "top",
  className,
  contentClassName,
  delayDuration = 200,
  ariaLabel,
  buttonTrigger = false,
}) => {
  useTranslation();
  const portalContainer = React.useContext(TooltipPortalContainerContext);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);

  // Detect touch device - run once on mount and update on first touch
  React.useEffect(() => {
    // Check for touch capability
    const checkTouch = () => {
      const hasTouch = 'ontouchstart' in window ||
                       navigator.maxTouchPoints > 0 ||
                       window.matchMedia('(pointer: coarse)').matches;
      setIsTouchDevice(hasTouch);
    };

    checkTouch();

    // Also detect on first actual touch event (more reliable on some devices)
    const handleFirstTouch = () => {
      setIsTouchDevice(true);
      window.removeEventListener('touchstart', handleFirstTouch);
    };

    window.addEventListener('touchstart', handleFirstTouch, { passive: true, once: true });

    return () => {
      window.removeEventListener('touchstart', handleFirstTouch);
    };
  }, []);

  const contentStyles = cn(
    "z-[100] max-h-[min(76vh,42rem)] max-w-xs overflow-x-hidden overflow-y-auto overscroll-contain [scrollbar-gutter:stable] rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-xl outline-none focus-visible:ring-2 focus-visible:ring-white/70 sm:max-w-sm sm:text-sm",
    "animate-in fade-in-0 zoom-in-95",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
    "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
    "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
    contentClassName
  );

  // On touch devices, use Popover (click-based)
  if (isTouchDevice) {
    return (
      <PopoverPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            className={cn("inline-flex cursor-pointer touch-manipulation", className)}
            aria-label={trDisplay(ariaLabel || 'Mostra informazioni')}
            data-testid="mobile-tooltip-trigger"
          >
            {children}
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal container={portalContainer}>
          <PopoverPrimitive.Content
            side={side}
            sideOffset={4}
            collisionPadding={12}
            align="center"
            className={contentStyles}
            tabIndex={0}
            onOpenAutoFocus={(e) => e.preventDefault()}
            data-testid="mobile-tooltip-content"
          >
            {content}
            <PopoverPrimitive.Arrow className="fill-slate-900" />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }

  // On desktop, use Tooltip (hover-based)
  const DesktopTrigger = buttonTrigger ? 'button' : 'span';
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipPrimitive.Root disableHoverableContent={false}>
        <TooltipPrimitive.Trigger asChild>
          <DesktopTrigger
            type={buttonTrigger ? 'button' : undefined}
            className={cn("inline-flex cursor-help", className)}
            aria-label={trDisplay(ariaLabel || 'Mostra informazioni')}
            tabIndex={0}
            data-testid="desktop-tooltip-trigger"
          >
            {children}
          </DesktopTrigger>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={portalContainer}>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={4}
            collisionPadding={12}
            className={contentStyles}
            tabIndex={0}
            data-testid="desktop-tooltip-content"
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-slate-900" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
};

/**
 * Info icon with mobile-friendly tooltip
 * Commonly used pattern throughout the app for explanatory tooltips
 */
const InfoTooltip = ({
  content,
  iconClassName = "h-4 w-4 text-slate-400 hover:text-slate-600 transition-colors",
  side = "top",
  ...props
}) => {
  useTranslation();
  return (
    <MobileTooltip content={content} side={side} {...props}>
      <HelpCircleIcon className={iconClassName} />
    </MobileTooltip>
  );
};

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  MobileTooltip,
  InfoTooltip
};
